import axios from 'axios';
import { BASE_URL } from './api';
import Cookies from 'js-cookie';
import { cookie } from 'constant';
import { decrypt } from 'utils/crypto';
import { clearAuthSession } from 'services/authSession';

const httpClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

let isHandlingUnauthorized = false;
let authTokenRefresher: (() => Promise<string>) | null = null;

export const registerAuthTokenRefresher = (refresher: () => Promise<string>) => {
  authTokenRefresher = refresher;

  return () => {
    if (authTokenRefresher === refresher) {
      authTokenRefresher = null;
    }
  };
};

httpClient.interceptors.request.use(
  async (config) => {
    const token = Cookies.get(cookie.token);
    const profile = Cookies.get(cookie.profile);

    if (token && !config.headers.Authorization) {
      let binusianId = '0000000000';

      if (profile) {
        try {
          const parsedProfile = JSON.parse(await decrypt(profile));
          binusianId = parsedProfile?.adminID || '0000000000';
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Failed to parse profile cookie:', error);
        }
      }

      config.headers.Authorization = `Bearer ${token}`;
      config.headers['Binusian-ID'] = binusianId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error?.config || {};
    const skipRedirect = cfg.headers && cfg.headers['X-Skip-Auth-Redirect'];

    if (error.response?.status === 401 && !skipRedirect && !cfg._retry && authTokenRefresher) {
      cfg._retry = true;

      try {
        const token = await authTokenRefresher();
        cfg.headers.Authorization = `Bearer ${token}`;
        return httpClient(cfg);
      } catch {
        // Continue to the centralized unauthorized handling below.
      }
    }

    if (error.response?.status === 401 && !skipRedirect && !isHandlingUnauthorized) {
      isHandlingUnauthorized = true;
      clearAuthSession();
      window.location.replace('/login?reason=unauthorized');
    }
    return Promise.reject(error);
  }
);

export default httpClient;
